// eslint-disable-next-line jira/restricted/react
import React, { PureComponent } from 'react';
import scrollIntoViewIfNeeded from 'scroll-into-view-if-needed';
import FocusRing from '@atlaskit/focus-ring';
import Heading from '@atlaskit/heading';
import { Flex, xcss } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import BluePrintComponent from '@atlassian/jira-illustrations/src/ui/adg4/brand/spots/objects/components/add-on-v1';
import CustomComponent from '@atlassian/jira-illustrations/src/ui/adg4/brand/spots/objects/components/gears';
import { injectIntlV2 as injectIntl } from '@atlassian/jira-intl/src/v2/inject.tsx';
import type { IntlShapeV2 as IntlShape } from '@atlassian/jira-intl/src/v2/types.tsx';
import { type BlueprintModuleKey, customBlueprintModuleKey } from '../../../../../../model';
import messages from './messages';
import { Container, Content, Description } from './styled';

export type BlueprintOptionsProps = {
	icon: string;
	blueprintModuleKey: BlueprintModuleKey;
	name: string;
	description: string;
	active: boolean;
	onSelect: (arg1: BlueprintModuleKey) => void;
};

export type Props = BlueprintOptionsProps & {
	intl: IntlShape;
};

// eslint-disable-next-line jira/react/no-class-components
class BlueprintOptions extends PureComponent<Props> {
	static defaultProps = {
		active: false,
	};

	componentDidUpdate(prevProps: Props): void {
		// If this blueprint is selected but not focused (i.e. selected using keyboard), we want to focus and scroll into view
		if (
			this.props.active &&
			!prevProps.active &&
			this.containerElement !== document.activeElement
		) {
			this.containerElement.focus();
			scrollIntoViewIfNeeded(this.containerElement);
		}
	}

	onSetContainerElement = (containerElement: HTMLDivElement): void => {
		this.containerElement = containerElement;
	};

	onSelect = (): void => {
		// Prevent the select action from being fired again in case the blueprint is already selected by using keyboard.
		if (!this.props.active) {
			this.props.onSelect(this.props.blueprintModuleKey);
		}
	};

	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	getHeading(): any {
		const {
			intl: { formatMessage },
			blueprintModuleKey,
			name,
		} = this.props;
		if (blueprintModuleKey === customBlueprintModuleKey) {
			return <Heading size="xsmall">{formatMessage(messages.createCustomRule)}</Heading>;
		}
		return <Heading size="xsmall">{name}</Heading>;
	}

	// @ts-expect-error - TS2564 - Property 'containerElement' has no initializer and is not definitely assigned in the constructor.
	containerElement: HTMLDivElement;

	render() {
		const { description, active, blueprintModuleKey } = this.props;
		const heading = this.getHeading();
		return (
			<FocusRing>
				<Container
					ref={this.onSetContainerElement}
					active={active}
					// @ts-expect-error - TS2322 - Type 'string' is not assignable to type 'number | undefined'.
					tabIndex="-1"
					onFocus={this.onSelect}
				>
					<Flex justifyContent="center" direction="column" xcss={iconContainerCss}>
						{blueprintModuleKey === customBlueprintModuleKey ? (
							<CustomComponent aria-hidden alt="" />
						) : (
							<BluePrintComponent aria-hidden alt="" />
						)}
					</Flex>

					<Content>
						{heading}
						{description && <Description>{description}</Description>}
					</Content>
				</Container>
			</FocusRing>
		);
	}
}

export default injectIntl(BlueprintOptions);

const iconContainerCss = xcss({
	width: token('space.600', '48px'),
	height: token('space.600', '48px'),
	maxWidth: token('space.600', '48px'),
	margin: 'space.100',
	padding: 'space.100',
	flexGrow: 0,
	flexShrink: 0,
});
